import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { InputFLUncontrolled } from "Components/Inputs/InputFL";
import {
   Dropdown,
   DropdownMenu,
   DropdownToggle,
} from "Components/ReactstrapSlim";
import UserMenuList from "./UserMenuList";
import { UserInputProps } from "./UserInputProps";
import { User } from "./User";
import classes from "./UserInput.module.scss";
import { UserPictureCache } from "Hooks";

interface UserInputUncontrolledProps extends UserInputProps {
   value: User | null;
   userPictureCache: UserPictureCache,
   errorMessage?: string;
   onChange?: (user: User | null) => void;
   onClick?: (user: User | null) => void;
   dropdownMinWidth?: number;
   ariaLabel?: string;
}

const DEBOUNCE_DELAY = 300;

const UserInput = forwardRef<HTMLInputElement, UserInputUncontrolledProps>(({
   name,
   value,
   userPictureCache,
   onChange,
   getUsers,
   onClick,
   onFocus,
   onBlur,
   errorMessage,
   dropdownMinWidth,
   ariaLabel,
   ...inputProps
}, ref) => {
   const inputRef = useRef<HTMLInputElement | null>(null);
   const [inputValue, setInputValue] = useState(value?.fullName ?? "");
   const [users, setUsers] = useState<User[]>([]);
   const [loading, setLoading] = useState(false);
   const [focused, setFocused] = useState(false);
   const [toggleOpen, setToggleOpen] = useState(false);
   const focusTimerId = useRef<ReturnType<typeof setTimeout>>();

   const isOpen = !loading && (toggleOpen || focused);

   useEffect(() => {
      const abortController = new AbortController();

      const timerId = setTimeout(async () => {
         if (getUsers) {
            setLoading(true);
            try {
               const users = await getUsers(inputValue, abortController.signal);
               setUsers(users);
            } finally {
               setLoading(false);
            }
         }
      }, DEBOUNCE_DELAY);

      return () => {
         clearTimeout(timerId);
         abortController.abort();
      };
   }, [getUsers, inputValue]);

   useEffect(() => () => {
      if (focusTimerId.current) {
         clearTimeout(focusTimerId.current);
      }
   }, [])

   const handleNameChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
         const inputValue = e.target.value;
         setInputValue(inputValue);
         onChange?.({ ...value, fullName: inputValue, id: undefined });
      },
      [onChange, value]
   );

   const handleToggle = useCallback(() => {
      setToggleOpen((s) => !s);
   }, []);

   const handleClick = useCallback(
      (user: User) => {
         onChange?.({ ...user });
         onClick?.({ ...user });
         setInputValue(user.fullName);
         handleToggle();
      },
      [onChange, onClick, handleToggle]
   );

   const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
         onFocus?.(e);
         setFocused(true);
         handleToggle();
      },
      [onFocus, handleToggle]
   );

   const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
         onBlur?.(e);
         focusTimerId.current = setTimeout(() => setFocused(false), 200);
      },
      [onBlur]
   );

   const setRef = (element: HTMLInputElement) => {
      if (typeof ref === "function") {
         ref(element);
      } else if (ref) {
         ref.current = element;
      }
      inputRef.current = element;
   };

   return (
      <Dropdown isOpen={isOpen} toggle={handleToggle} direction="down">
         <DropdownToggle tag="div" disabled>
            <InputFLUncontrolled
               name={name}
               value={inputValue}
               onChange={handleNameChange}
               innerRef={setRef}
               isInvalid={!!errorMessage}
               validationMessage={errorMessage}
               onFocus={handleFocus}
               onBlur={handleBlur}
               aria-label={ariaLabel}
               {...inputProps}
            />
         </DropdownToggle>
         <DropdownMenu
            className={classes.dropdownMenu}
            style={{ minWidth: dropdownMinWidth ?? inputRef.current?.clientWidth }}
            positionFixed
            modifiers={{ preventOverflow: { boundariesElement: "viewport" } }}
         >
            <UserMenuList
               userPictureCache={userPictureCache}
               users={users}
               onClick={handleClick}
               className={classes.dropdownUserMenuList}
               role="menuitem"
            />
         </DropdownMenu>
      </Dropdown>
   );
});

export default UserInput;
